import type { IPresentationData, ISlideFileData } from '@tencent/slide-sdk-model';
import Axios from 'axios';
import { MetaOfTemplate, Thumbnail } from '../../change-template-service/index.interface';

const templateCacheMap: Record<string, ISlideTemplateData> = {};
const templateThumbnailCacheMap: Record<string, Thumbnail> = {};
const FETCH_TEMPLATE_API = '/v2/mall/template/content';
const FETCH_TEMPLATE_API_2 = '/api/template/content';
const FETCH_THUMBNAIL_API = '/api/template/thumbnail/snap';

type ISlideTemplateData = ISlideFileData & {
    metaData: MetaOfTemplate
};

/** 从模板商城拉取模板数据 */
export const fetchTemplateById = async (templateId: string): Promise<ISlideTemplateData> => {
    if (templateCacheMap[templateId]) return templateCacheMap[templateId];

    const { data } = await Axios.post(FETCH_TEMPLATE_API, {
        id: templateId,
        is_custom: false,
        version: 2,
    });

    if (data.retcode !== 0) throw `get template failed retcode: ${data.retcode}`;
    try {
        // eslint-disable-next-line prefer-destructuring
        const { bytesText } = data.result.docData.msgAtext[0];
        const slideFileData = JSON.parse(atob(bytesText));
        slideFileData.metaData = data.result.item_meta;
        templateCacheMap[templateId] = slideFileData;
        return slideFileData;
    } catch (error) {
        console.error('get template failed', error);
        throw error;
    }
};

/**
 * 拉取模版数据，这个接口会忽略vip阻断，因此拉取的数据不能缓存，且这个接口后续可能被下掉
 * @param templateId
 */
export const fetchTemplateByIdByNonVip = async (templateId: string): Promise<ISlideTemplateData> => {
    if (templateCacheMap[templateId]) return templateCacheMap[templateId];

    const { data } = await Axios.post(FETCH_TEMPLATE_API_2, {
        id: templateId,
        is_custom: false,
        version: 2,
    });

    if (data.retcode !== 0) throw `get template failed retcode: ${data.retcode}`;
    try {
        // eslint-disable-next-line prefer-destructuring
        const { bytes_text } = data.result.content.doc_data.msg_atext[0];
        const slideFileData = JSON.parse(atob(bytes_text));
        slideFileData.metaData = data.result.item_meta;
        return slideFileData;
    } catch (error) {
        console.error('get template failed', error);
        throw error;
    }
};

export const fetchThumbnailById = async (templateId: string): Promise<Thumbnail> => {
    if (templateCacheMap[templateId]?.metaData) return templateCacheMap[templateId].metaData;
    if (templateThumbnailCacheMap[templateId]) return templateThumbnailCacheMap[templateId];
    try {
        const { data } = await Axios.post(FETCH_THUMBNAIL_API, {
            id: templateId,
        });
        if (data.retcode !== 0) throw `get template thumbnail failed retcode: ${data.retcode}`;
        templateThumbnailCacheMap[templateId] = data.result;
        return data.result;
    } catch (error) {
        console.error('get template thumbnail failed', error);
        throw error;
    }
};


const DATA_GET_API = '/dop-api/slide/data/get';

/**
 * 最好测试环境用，拉取会文档访问历史记录
 * @param docId
 */
export async function fetchDocDataById(docId: string): Promise<ISlideFileData> {
    try {
        const res = await Axios.get(DATA_GET_API, {
            params: {
                normal: 1,
                id: docId,
                needPaginate: 0,
            },
        });

        const rawText = res.data.data.collab_client_vars.initialAttributedText.text;
        return JSON.parse(rawText);
    } catch (error) {
        console.error('get doc data failed', error);
        throw error;
    }
}


const CDN_PATH = 'https://docs.gtimg.com/slide/json/';
const filenameByType = {
    component: 'template-component-7b297a1a.json',
    cover: 'template-cover-f2b0164.json',
};

export async function fetchTemplateFromCdn(template: keyof typeof filenameByType): Promise<IPresentationData> {
    try {
        const res = await downloadJSON(`${CDN_PATH}${filenameByType[template]}`);
        return res;
    } catch (error) {
        console.error('fetch template from cdn failed', error);
        throw error;
    }
}

async function downloadJSON(url: string) {
    try {
        const response = await fetch(url);
        // 检查响应状态
        if (!response.ok) {
            throw new Error(`network error：${response.status}`);
        }
        // 解析 JSON 数据
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('download json failed:', error);
        throw error;
    }
}
